/**
 * Analytics Service
 *
 * Core singleton responsible for:
 * - Event queuing and batching
 * - Flushing on interval, max queue, or page unload
 * - POST to /events via apiClient (authenticated)
 * - Retry failed batches (up to 3 times with exponential backoff)
 * - Dev-mode console logging
 *
 * @module @ideoz/core/analytics/service
 */

import type { AnalyticsEvent, EventBatchPayload, GetReduxState } from './analytics.types';
import { analyticsConfig } from './analytics.config';
import { enrichEvent, setReduxStateAccessor, isUserLoggedIn } from './analytics.enricher';
import { getPlatformSource } from '../utils/environment';
import { isFreeModeEnabled } from '../utils/free-mode';

// ==================== State ====================

let queue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;
let apiClient: { post: (url: string, data: unknown) => Promise<unknown> } | null = null;
let eventsEndpoint = '';

// ==================== UUID Helper ====================

function generateBatchId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ==================== Initialization ====================

/**
 * Initialize the analytics service.
 * Call once at app startup after the Redux store is created.
 *
 * @param client - The axios apiClient instance (from @ideoz/core/api/client)
 * @param endpoint - Full URL for the events endpoint (e.g., ApiUrls.events)
 * @param getState - Function that returns the current Redux state
 */
export function initAnalytics(
  client: { post: (url: string, data: unknown) => Promise<unknown> },
  endpoint: string,
  getState: GetReduxState,
): void {
  if (initialized) return;

  // FREE_MODE_ENABLED bypass — skip Mixpanel entirely so no `/events`
  // batches ever fire. Mirrors the backend FREE_MODE guards.
  if (isFreeModeEnabled()) {
    initialized = true;
    return;
  }

  apiClient = client;
  eventsEndpoint = endpoint;
  setReduxStateAccessor(getState);

  // Start periodic flush
  const { batchIntervalMs } = analyticsConfig.settings;
  flushTimer = setInterval(() => {
    flushQueue();
  }, batchIntervalMs);

  // Flush on page unload (web only)
  if (typeof window !== 'undefined') {
    const handleUnload = () => flushQueue();
    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushQueue();
      }
    });
  }

  initialized = true;

  if (isDev()) {
    console.log('📊 Analytics initialized', { endpoint, batchIntervalMs });
  }
}

/**
 * Shut down the analytics service (for testing / cleanup).
 */
export function destroyAnalytics(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flushQueue(); // Send remaining events
  queue = [];
  initialized = false;
  apiClient = null;
}

// ==================== Track ====================

/**
 * Track a single analytics event.
 * The event is enriched with context then queued for batching.
 *
 * @param eventName - Mixpanel event name (e.g. "project.created")
 * @param properties - Event-specific properties
 */
export function trackEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
): void {
  try {
    if (analyticsConfig.settings.trackOnlyWhenLoggedIn && !isUserLoggedIn()) {
      if (isDev() && analyticsConfig.settings.enableInDev) {
        console.log('📊 Analytics Event skipped (trackOnlyWhenLoggedIn, user not logged in):', eventName);
      }
      return;
    }

    const event = enrichEvent(eventName, properties);

    if (isDev() && analyticsConfig.settings.enableInDev) {
      console.log('📊 Analytics Event:', event.event_name, event.properties);
    }

    queue.push(event);

    // Force-flush if queue exceeds max
    if (queue.length >= analyticsConfig.settings.maxQueueSize) {
      flushQueue();
    }
  } catch (error) {
    console.warn('Analytics trackEvent error (non-blocking):', error);
  }
}

// ==================== Flush / Send ====================

/**
 * Flush the current event queue — send as a batch to the backend.
 */
function flushQueue(): void {
  if (queue.length === 0) return;
  if (!isEnabled()) {
    if (isDev() && analyticsConfig.settings.enableInDev) {
      console.log(`📊 Analytics flush skipped (not enabled). ${queue.length} events discarded.`);
    }
    queue = [];
    return;
  }

  const batch = queue.splice(0, analyticsConfig.settings.batchSize);
  if (batch.length === 0) return;

  const payload: EventBatchPayload = {
    events: batch,
    batch_id: generateBatchId(),
    sent_at: new Date().toISOString(),
    source: getPlatformSource(),
  };

  sendBatch(payload, 0);

  // If there are still events remaining, flush again
  if (queue.length > 0) {
    flushQueue();
  }
}

/**
 * Send a batch to the API with retry logic.
 */
async function sendBatch(payload: EventBatchPayload, attempt: number): Promise<void> {
  const MAX_RETRIES = 3;

  if (!apiClient || !eventsEndpoint) {
    if (isDev()) {
      console.warn('📊 Analytics: no API client or endpoint configured');
    }
    return;
  }

  try {
    await apiClient.post(eventsEndpoint, payload);
  } catch (error) {
    if (attempt < MAX_RETRIES) {
      const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
      setTimeout(() => sendBatch(payload, attempt + 1), delay);
    } else {
      console.warn('📊 Analytics batch send failed after retries:', error);
    }
  }
}

// ==================== Helpers ====================

function isDev(): boolean {
  try {
    return (
      import.meta.env?.MODE === 'development' ||
      import.meta.env?.VITE_NODE_ENV === 'development'
    );
  } catch {
    return false;
  }
}

function isEnabled(): boolean {
  try {
    const env = import.meta.env?.MODE ?? import.meta.env?.VITE_NODE_ENV ?? 'development';
    return analyticsConfig.settings.enabledEnvironments.includes(env);
  } catch {
    return false;
  }
}
